import React from 'react';
import PropTypes from 'prop-types';

// API
import { useConcepts } from '../../api/hooks/useConcepts';

// Config
import { uiConfig } from '../../../conf/concepts';
import { listTypes } from '../../../conf/enums';

// Components
import { DualList, DualListRowCode, DualListRowName } from '../../../../components/DualList';
import { Pill } from '../../../../components/Pill';
import { Button } from '../../../../components/Button';
import { Dialog } from '../../../../components/Dialog';
import { DialogHeader } from '../../../../components/DialogHeader';
import { DialogBody } from '../../../../components/DialogBody';
import { DialogFooter } from '../../../../components/DialogFooter';

const reduceListItems = (listItems = []) =>
    listItems.reduce(
        (acc, listItem) => ({
            ...acc,
            [listItem?.openCdsConceptDTO?.codeId]: listItem.dtoState !== 'DELETED'
        }),
        {}
    );

const wrapConcept = ({ concept, listId }) => ({
    // createDatetime: format(new Date(), "yyyy-MM-dd'T'HH:mm:ssxxxx"),
    // createId: concept.createId,
    dtoState: 'NEW',
    itemKey: concept.code.toLowerCase(),
    itemType: listTypes.AD_HOC_CONCEPT,
    // lastModDatetime: format(new Date(), "yyyy-MM-dd'T'HH:mm:ssxxxx"),
    // lastModId: concept.createId,
    listId: listId,
    openCdsConceptDTO: concept,
    uuid: uuid4()
});

export const ConceptTableModal = ({ sessionToken, closeDialog, onApply, listId, currentListItems = [] }) => {
    const [selected, setSelected] = React.useState({});
    const selectConcept = React.useCallback(
        (id) => {
            if (!Object.hasOwn(selected, id)) {
                setSelected({
                    ...selected,
                    [id]: true
                });
            } else {
                setSelected({
                    ...selected,
                    [id]: !selected[id]
                });
            }
        },
        [selected, setSelected]
    );
    const conceptsQuery = useConcepts(sessionToken);
    const CloseDialog = () => {
        return typeof closeDialog === 'function' ? closeDialog() : null;
    };
    const selectedCount = React.useMemo(() => Object.values(selected).filter(Boolean).length, [selected]);

    React.useEffect(() => {
        setSelected(reduceListItems(currentListItems));
    }, [currentListItems, setSelected]);

    const prepareSelection = React.useCallback(() => {
        const currentlySelected = [...currentListItems];
        const openCdsConceptDTOUpdated = Object.entries(selected).reduce((acc, [currKey, currVal]) => {
            const found = currentlySelected.find((listItem) => listItem?.openCdsConceptDTO?.codeId === currKey);

            if (found) {
                return !currVal && found.dtoState === 'NEW'
                    ? acc
                    : [
                          ...acc,
                          {
                              ...found,
                              dtoState: currVal ? found.dtoState : 'DELETED'
                          }
                      ];
            } else if (currVal) {
                const concept = conceptsQuery?.data.find((concept) => currKey === concept[uiConfig.primaryKey]);
                return [
                    ...acc,
                    wrapConcept({
                        concept,
                        listId
                    })
                ];
            } else {
                return acc;
            }
        }, []);

        onApply(openCdsConceptDTOUpdated);
        closeDialog();
    }, [selected, currentListItems, conceptsQuery, listId, onApply]);

    const renderRowContents = (item, isSelected) => {
        return (
            <>
                <DualListRowCode>
                    <Pill>{item.code}</Pill>
                </DualListRowCode>
                <DualListRowName>{item.displayName}</DualListRowName>
            </>
        );
    };

    return (
        <Dialog close={CloseDialog} size="full" visible id="list-manager-concept-table-modal">
            <DialogHeader close={CloseDialog}>Concept Table</DialogHeader>
            <DialogBody style={{ paddingBottom: 0 }}>
                <DualList
                    allItems={conceptsQuery?.data ?? []}
                    selected={selected}
                    onSelect={(item) => selectConcept(item.codeId)}
                    isLoading={conceptsQuery?.isLoading}
                    renderRowContents={renderRowContents}
                    primaryKey={'codeId'}
                    contentType={'concepts'}
                    searchableProperties={['code', 'displayName']}
                />
            </DialogBody>
            <DialogFooter>
                <Button text appearance="primary" onClick={prepareSelection} disabled={!selectedCount} loading={false}>
                    Apply
                </Button>
                <Button text appearance="secondary" onClick={CloseDialog}>
                    Cancel
                </Button>
            </DialogFooter>
        </Dialog>
    );
};

export default ConceptTableModal;

ConceptTableModal.propTypes = {
    sessionToken: PropTypes.string,
    listId: PropTypes.string,
    closeDialog: PropTypes.func,
    onApply: PropTypes.func,
    currentListItems: PropTypes.arrayOf(PropTypes.object)
};
